import { useTranslation } from 'react-i18next';
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import { NotificationDropdown } from "@/components/layout/NotificationDropdown";
import { ProfileMenu } from "@/components/layout/ProfileMenu";
import { Tooltip } from "@/components/ui/Tooltip";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import { toggleSidebar } from "@/store/appSlice";
import { cn } from "@/utils/cn";
import type { ReactNode } from "react";

interface HeaderProps {
  /** Organization code shown on the left (replaces module name). */
  organizationCode?: string;
  /**
   * Fallback label when no organization code (e.g. Master Admin).
   * @deprecated Prefer organizationCode for org workspaces.
   */
  moduleLabel?: string;
  /** @deprecated Use organizationCode or moduleLabel */
  title?: string;
  /** Left of header — app launcher, etc. */
  leading?: ReactNode;
  /** Right of header — company switcher, store selector, etc. */
  trailing?: ReactNode;
  extra?: ReactNode;
  onOpenSearch?: () => void;
  /** Keep the hamburger visible on desktop (used by the master workspace). */
  persistentSidebarToggle?: boolean;
}

export function Header({
  organizationCode,
  moduleLabel,
  title,
  leading,
  trailing,
  extra,
  onOpenSearch,
  persistentSidebarToggle = false,
}: HeaderProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const masterAdmin = useAppSelector((s) => s.auth.user?.masterAdmin === true);
  const brandLabel = (organizationCode ?? moduleLabel ?? title)?.trim();

  return (
    <header className="workspace-header" role="banner">
      <div className="workspace-header-start">
        {/* Tablet / mobile only — laptop uses the nav right-edge open/close control */}
        <button
          type="button"
          onClick={() => dispatch(toggleSidebar())}
          className={cn(
            "workspace-header-menu-btn",
            !persistentSidebarToggle && "lg:hidden",
          )}
          aria-label={t('labels.toggleSidebar')}
        >
          <MenuIcon sx={{ fontSize: 22 }} />
        </button>
        {leading}
        {brandLabel ? (
          <span
            className={organizationCode ? 'workspace-header-org' : 'workspace-header-module'}
            title={brandLabel}
          >
            {brandLabel}
          </span>
        ) : null}
        {trailing ? (
          <div className="workspace-header-context flex min-w-0 items-center gap-1.5 sm:gap-2">
            {trailing}
          </div>
        ) : null}
      </div>
      <div className="workspace-header-end">
        <div className="workspace-header-tools">
          {extra}
          {onOpenSearch && (
            <Tooltip content={t('labels.searchCtrlK')}>
              <button
                type="button"
                className="workspace-header-ghost-btn"
                onClick={onOpenSearch}
                aria-label={t('labels.searchCtrlK')}
              >
                <SearchIcon sx={{ fontSize: 22 }} />
              </button>
            </Tooltip>
          )}
          {!masterAdmin && <NotificationDropdown variant="header" />}
        </div>
        <div className="workspace-header-account">
          <ProfileMenu variant="header" />
        </div>
      </div>
    </header>
  );
}
